import type { Environment } from '~/engine/data/Environment';
import type { StructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import type { ListedStructure } from '../../contract';
import { toListed, type StructureFacts } from './pipeline';

/**
 * The read projection shared by every op's engine adapter: the current
 * structure-set read model + the live engine → the domain facts the safety
 * core validates.
 * Emptiness defers to the engine's authoritative rule (no pixel content, not
 * merely a missing texture object); point ROIs are flagged separately by type.
 * A missing volume reads as NaN (unrepresentable as a number), which the
 * safety core treats as invalid.
 */
export function projectStructureFacts(
    structureSet: StructureSetReadModel | undefined,
    env: Environment | null
): StructureFacts[] {
    return (structureSet?.roiArray ?? []).map((roi) => {
        return {
            roiNumber: roi.roiNumber,
            name: roi.name,
            type: roi.interpretedType,
            volumeCm3: env?.getRoiVolumeCm3(roi.roiNumber) ?? Number.NaN,
            isEmpty:
                !roi.isPointRoi && (env?.isRoiEmpty(roi.roiNumber) ?? true),
        };
    });
}

/**
 * The same projection shaped for the model-facing `list_structures` output —
 * the read tool and the mutation gates share ONE projection so they cannot
 * drift. A missing volume becomes null (NaN is unrepresentable in JSON).
 */
export function projectListedStructures(
    structureSet: StructureSetReadModel | undefined,
    env: Environment | null
): ListedStructure[] {
    return projectStructureFacts(structureSet, env).map((facts) => ({
        ...toListed(facts),
        volumeCm3: Number.isNaN(facts.volumeCm3) ? null : facts.volumeCm3,
    }));
}
